import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, FileText, TrendingUp, Download } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { getPartnerConfig } from '../config/partners';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Composant pour l'icône "?" avec tooltip explicatif
const InfoIcon = ({ text }) => {
  const [showInfo, setShowInfo] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const buttonRef = React.useRef(null);
  
  const handleMouseEnter = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        x: rect.left + rect.width / 2,
        y: rect.bottom + 10
      });
    }
    setShowInfo(true);
  };
  
  return (
    <div className="relative inline-block ml-1 pdf-hidden">
      <button
        ref={buttonRef}
        className="inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-blue-500 rounded-full hover:bg-blue-600 focus:outline-none"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShowInfo(false)}
        onClick={handleMouseEnter}
      >
        ?
      </button>
      {showInfo && (
        <div 
          className="fixed p-4 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg shadow-xl" 
          style={{ 
            left: `${position.x}px`,
            top: `${position.y}px`,
            transform: 'translateX(-50%)',
            minWidth: '300px',
            maxWidth: '450px',
            whiteSpace: 'normal',
            lineHeight: '1.6',
            zIndex: 9999
          }}
        >
          {text}
          {/* Flèche pointant vers le haut */}
          <div className="absolute left-1/2 bottom-full w-0 h-0" style={{
            transform: 'translateX(-50%)',
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderBottom: '8px solid white',
            marginBottom: '-1px'
          }}></div>
        </div>
      )}
    </div>
  );
};

const PublicOfferView = () => {
  const { shareLink } = useParams();
  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [showTTC, setShowTTC] = useState(true); // Toggle HT/TTC, par défaut TTC (ignoré pour électricité)
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [partnerConfig, setPartnerConfig] = useState(getPartnerConfig('place_des_energies'));

  // Fonction pour générer le PDF via impression native
  const generatePDF = () => {
    if (!offer) return;
    
    // Ajouter la classe pour le mode impression
    document.body.classList.add('printing-pdf');
    
    // Déclencher l'impression
    window.print();
    
    // Retirer la classe après un délai
    setTimeout(() => {
      document.body.classList.remove('printing-pdf');
    }, 1000);
  };

  useEffect(() => {
    fetchOffer();
  }, [shareLink]);

  const fetchOffer = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/offers/public/${shareLink}`);
      setOffer(response.data);
      // Charger la configuration du partenaire
      const config = getPartnerConfig(response.data.partner || 'place_des_energies');
      setPartnerConfig(config);
    } catch (error) {
      console.error('Error fetching offer:', error);
    } finally {
      setLoading(false);
    }
  };

  // Descriptions des fournisseurs
  const getSupplierDescription = (supplierName) => {
    const name = supplierName?.toUpperCase() || '';
    const descriptions = {
      'SYNELVA': 'Synelva est un fournisseur d\'énergie français spécialisé dans l\'approvisionnement en électricité et gaz naturel pour les professionnels et les industriels. Engagé dans la transition énergétique, Synelva propose des offres d\'énergie verte et des solutions d\'efficacité énergétique adaptées aux besoins de ses clients.',
      'GAZ EUROPEEN': 'Gaz Européen est l\'un des principaux acteurs du marché de l\'énergie en France, proposant des solutions d\'approvisionnement en gaz naturel aux professionnels et aux industriels. Avec un engagement fort pour la transition énergétique, Gaz Européen offre des offres d\'énergie verte et des services d\'efficacité énergétique.',
      'GAZ DE BORDEAUX': 'Gaz de Bordeaux est une entreprise locale de distribution (ELD) qui opère dans la région bordelaise. Elle fournit du gaz naturel aux professionnels et aux particuliers tout en s\'engageant dans des solutions énergétiques durables et innovantes pour accompagner la transition énergétique.',
      'ENDESA': 'Endesa France est la filiale française d\'Endesa, l\'un des principaux acteurs du secteur énergétique en Europe. Spécialisée dans la fourniture d\'électricité et du gaz naturel aux professionnels et aux industriels en France, Endesa s\'engage activement dans la transition énergétique en proposant des offres d\'énergie verte et des solutions d\'efficacité énergétique.',
      'DYNEFF': 'Dyneff est un groupe énergétique français indépendant, fondé en 1968 dans le sud de la France. Spécialisé dans la distribution de carburants, de gaz naturel et de fioul, Dyneff s\'adresse aux professionnels et aux particuliers. Le groupe s\'engage dans la transition énergétique en proposant des solutions d\'énergie renouvelable et des services d\'efficacité énergétique pour accompagner ses clients vers une consommation plus responsable.',
      'EDF': 'EDF (Électricité de France) est le premier producteur et fournisseur d\'électricité en France et l\'un des leaders mondiaux du secteur énergétique. Entreprise historique créée en 1946, EDF propose des offres d\'électricité et de gaz naturel aux particuliers, professionnels et collectivités. Engagé dans la transition énergétique, EDF développe les énergies renouvelables et accompagne ses clients vers une consommation plus responsable.',
      'ENGIE': 'Engie (anciennement GDF Suez) est un acteur majeur de l\'énergie en France et dans le monde. Spécialisé dans le gaz naturel, l\'électricité et les services énergétiques, Engie accompagne les particuliers et les professionnels dans leur transition énergétique. Le groupe propose des solutions innovantes d\'efficacité énergétique, d\'énergies renouvelables et de services pour réduire l\'empreinte carbone de ses clients.',
      'TOTAL': 'TotalEnergies est une compagnie multi-énergies mondiale, présente dans plus de 130 pays. En France, TotalEnergies fournit du gaz naturel et de l\'électricité aux particuliers et aux professionnels. Engagé dans la transition vers les énergies bas carbone, le groupe développe les énergies renouvelables (solaire, éolien, biogaz) et propose des solutions d\'efficacité énergétique pour accompagner ses clients.',
      'PICOTY': 'Picoty est un groupe familial français indépendant, fondé en 1963 dans le Limousin. Spécialisé dans la distribution de produits pétroliers, de gaz naturel et de granulés de bois, Picoty s\'adresse aux particuliers, agriculteurs et professionnels. Le groupe s\'engage dans la transition énergétique en proposant des solutions d\'énergies renouvelables et en accompagnant ses clients vers une consommation plus durable.',
      'SEFE': 'SEFE Energy (anciennement Gazprom Energy) est un fournisseur d\'énergie européen proposant du gaz naturel et de l\'électricité aux entreprises et aux industriels. Présent en France, SEFE se distingue par son expertise dans l\'approvisionnement énergétique des grands comptes et son engagement à proposer des solutions compétitives et flexibles adaptées aux besoins de chaque client professionnel.',
      'GME': 'GME (Garcia Munté Energía) est un fournisseur d\'énergie espagnol présent en France, spécialisé dans la commercialisation de gaz naturel et d\'électricité pour les professionnels et les industriels. Filiale du groupe Garcia Munté, GME propose des solutions énergétiques compétitives et un accompagnement personnalisé pour optimiser la consommation énergétique de ses clients.',
      'MINT': 'Mint Énergie est un fournisseur français d\'électricité verte, fondé en 2017. Filiale du groupe Budget Telecom, Mint Énergie propose des offres d\'électricité 100% verte à des prix compétitifs pour les particuliers et les professionnels. Engagé dans la transition énergétique, Mint Énergie se distingue par son approche digitale et ses offres transparentes sans engagement.',
      'LA BELLENERGIE': 'La Bellenergie est un fournisseur français d\'électricité verte et locale, fondé en 2019. La Bellenergie propose une électricité 100% renouvelable produite en France, avec une traçabilité totale de l\'origine de l\'énergie. Le fournisseur s\'engage pour une consommation responsable et accompagne ses clients professionnels et particuliers dans leur transition énergétique.',
      'PRIMEO': 'Primeo Énergie est un fournisseur d\'énergie suisse présent en France, spécialisé dans la fourniture d\'électricité et de gaz naturel aux professionnels et aux industriels. Filiale du groupe Primeo Energie AG, l\'entreprise propose des solutions énergétiques compétitives et accompagne ses clients dans leur transition vers des énergies plus durables.'
    };
    // Recherche flexible du nom du fournisseur
    if (name.includes('PRIMEO')) return descriptions['PRIMEO'];
    if (name.includes('MINT')) return descriptions['MINT'];
    if (name.includes('BELLENERGIE') || name.includes('BELLE ENERGIE') || name.includes('LABELLENERGIE')) return descriptions['LA BELLENERGIE'];
    if (name.includes('DYNEFF')) return descriptions['DYNEFF'];
    if (name.includes('SYNELVA')) return descriptions['SYNELVA'];
    if (name.includes('GAZ') && (name.includes('EUROPEEN') || name.includes('EUROPÉEN'))) return descriptions['GAZ EUROPEEN'];
    if (name.includes('GAZ') && name.includes('BORDEAUX')) return descriptions['GAZ DE BORDEAUX'];
    if (name.includes('ENDESA')) return descriptions['ENDESA'];
    if (name.includes('EDF')) return descriptions['EDF'];
    if (name.includes('ENGIE')) return descriptions['ENGIE'];
    if (name.includes('TOTAL')) return descriptions['TOTAL'];
    if (name.includes('PICOTY')) return descriptions['PICOTY'];
    if (name.includes('SEFE')) return descriptions['SEFE'];
    if (name.includes('GME') || name.includes('GARCIA') || name.includes('MUNTE') || name.includes('MUNTÉ')) return descriptions['GME'];
    return descriptions[supplierName] || 'Description non disponible pour ce fournisseur.';
  };

  const getSupplierLogo = (supplierName) => {
    const name = supplierName.toLowerCase();
    
    // Nouveaux logos
    if (name.includes('synelva')) {
      return 'https://customer-assets.emergentagent.com/job_offerviewer/artifacts/0pz4nmqg_logo%20synelva.png';
    } else if (name.includes('primeo')) {
      return 'https://customer-assets.emergentagent.com/job_144bfce2-2837-482e-b020-096a1d5058b6/artifacts/itwboz4b_Primeo.png';
    } else if (name.includes('mint')) {
      return 'https://customer-assets.emergentagent.com/job_144bfce2-2837-482e-b020-096a1d5058b6/artifacts/b6vdchfn_mint-energie-logo-vertical-standard%404x-002.png';
    } else if (name.includes('bellenergie') || name.includes('belle energie') || name.includes('labellenergie')) {
      return 'https://customer-assets.emergentagent.com/job_144bfce2-2837-482e-b020-096a1d5058b6/artifacts/csfb3sv9_labelleenergie-logo-1.png';
    } else if (name.includes('dyneff')) {
      return '/logos/dyneff.webp';
    } else if (name.includes('gaz') && (name.includes('europeen') || name.includes('européen'))) {
      return 'https://customer-assets.emergentagent.com/job_digital-offer-hub/artifacts/gpzzcgzl_gaz%20europeen.jfif';
    } else if (name.includes('grdf')) {
      return 'https://customer-assets.emergentagent.com/job_digital-offer-hub/artifacts/bj8lpstp_logo%20grdf.jfif';
    } else if (name.includes('enedis')) {
      return 'https://customer-assets.emergentagent.com/job_digital-offer-hub/artifacts/r5vpb35i_enedis%20logo.jfif';
    }
    
    // Anciens logos
    else if (name.includes('endesa')) {
      return '/logos/endesa.webp';
    } else if (name.includes('gme') || name.includes('garcia') || name.includes('munte') || name.includes('munté')) {
      return 'https://customer-assets.emergentagent.com/job_smartoffer-app/artifacts/bo4d14rf_GME_100_since-1922-1-1.svg';
    } else if (name.includes('gaz') && name.includes('bordeaux')) {
      return 'https://customer-assets.emergentagent.com/job_digital-offer-hub/artifacts/ctnf6t0r_Gaz%20de%20Bordeaux.png';
    } else if (name.includes('edf')) {
      return 'https://customer-assets.emergentagent.com/job_17785def-22c9-4cc5-bd4e-d2c76a4b5880/artifacts/lr1jq9dx_edf.jfif';
    } else if (name.includes('engie')) {
      return 'https://customer-assets.emergentagent.com/job_17785def-22c9-4cc5-bd4e-d2c76a4b5880/artifacts/h3s6bqxa_engie.jfif';
    } else if (name.includes('total')) {
      return 'https://customer-assets.emergentagent.com/job_17785def-22c9-4cc5-bd4e-d2c76a4b5880/artifacts/0i5h745t_Total.jfif';
    }
    
    return null;
  };

  const formatPrice = (price) => {
    // Format français : espace pour milliers, virgule pour décimales
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true
    }).format(price).replace(/\u202F/g, ' '); // Remplace l'espace insécable par un espace normal
  };

  // Helper pour appliquer TVA si nécessaire
  // Pour l'électricité: jamais de TVA (toujours HT)
  // Pour le gaz: selon le toggle showTTC
  const applyVAT = (value, includeTTC = showTTC) => {
    // L'électricité est toujours en HT, pas de TVA
    if (offer?.energy_type === 'electricity') {
      return value;
    }
    return includeTTC ? value * 1.2 : value;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: partnerConfig.colors.primary }} />
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Offre non trouvée</h1>
          <p className="text-gray-600">Le lien que vous avez suivi n'est pas valide</p>
        </div>
      </div>
    );
  }

  // Filtrer les fournisseurs valides (exclure ceux avec total_ttc = 0 ou undefined)
  // Pour l'électricité, on utilise total_ht si total_ttc n'est pas disponible
  const validSuppliers = offer.suppliers.filter(supplier => {
    if (offer.energy_type === 'electricity') {
      // Pour l'électricité, vérifier total_ht ou total_supply_ht
      return (supplier.total_ht && supplier.total_ht > 0) || (supplier.total_supply_ht && supplier.total_supply_ht > 0);
    }
    return supplier.total_ttc && supplier.total_ttc > 0;
  });

  // Calculer budget actuel depuis client_info
  // Pour l'électricité, utiliser current_budget_detail.total_ht si disponible
  const getBudgetActuel = () => {
    if (offer.energy_type === 'electricity') {
      if (offer.client_info?.current_budget_detail?.total_ht > 0) {
        return offer.client_info.current_budget_detail.total_ht;
      }
      // Fallback: calculer depuis les budgets électricité
      const elecBudgets = offer.client_info?.current_budget_detail?.electricity_budgets;
      if (elecBudgets) {
        const fourniture = (elecBudgets.hph || 0) + (elecBudgets.hch || 0) + (elecBudgets.hpe || 0) + (elecBudgets.hce || 0) + (elecBudgets.pte || 0);
        const taxes = (offer.client_info.current_budget_detail?.turpe_budget || 0) + 
                      (offer.client_info.current_budget_detail?.cta_tax || 0) + 
                      (offer.client_info.current_budget_detail?.cspe_budget || 0) +
                      (offer.client_info.current_budget_detail?.capacity_budget || 0);
        return fourniture + taxes + (offer.client_info.current_budget_detail?.subscription_price || 0) * 12;
      }
    }
    return offer.client_info?.current_budget_ttc || validSuppliers[0]?.total_ttc || 0;
  };
  const budgetActuel = getBudgetActuel();
  
  // Préparer les données pour le graphique en barres empilées (max 3 offres valides)
  const chartData = validSuppliers.slice(0, 3).map((supplier, index) => {
    let fourniture, acheminement, taxes;
    
    if (offer.energy_type === 'electricity') {
      // Pour l'électricité, calculer la fourniture depuis les budgets par profil
      const elecBudgets = supplier.electricity_budgets || {};
      fourniture = (elecBudgets.hph || 0) + (elecBudgets.hch || 0) + (elecBudgets.hpe || 0) + (elecBudgets.hce || 0) + (elecBudgets.pte || 0);
      acheminement = (parseFloat(supplier.turpe_budget) || 0) + (parseFloat(supplier.subscription_budget_annual) || 0);
      // N'inclure CEE dans taxes que si non inclus dans molécule
      const ceeForTaxes = supplier.cee_included_in_molecule ? 0 : (parseFloat(supplier.cee_budget) || 0);
      taxes = (parseFloat(supplier.cta_tax) || 0) + (parseFloat(supplier.cspe_budget) || 0) + (parseFloat(supplier.capacity_budget) || 0) + ceeForTaxes;
    } else {
      // Pour le gaz
      fourniture = parseFloat(supplier.molecule_budget_annual) || 0;
      acheminement = (parseFloat(supplier.variable_term_budget_annual) || 0) + (parseFloat(supplier.subscription_budget_annual) || 0);
      // N'inclure CEE dans taxes que si non inclus dans molécule
      const ceeForTaxes = supplier.cee_included_in_molecule ? 0 : (parseFloat(supplier.cee_budget) || 0);
      taxes = (parseFloat(supplier.cta_tax) || 0) + (parseFloat(supplier.ticgn_tax) || 0) + ceeForTaxes;
    }
    
    return {
      name: `Offre #${index + 1}`,
      supplier: supplier.name,
      Fourniture: fourniture,
      Acheminement: acheminement,
      Taxes: taxes,
      logo: getSupplierLogo(supplier.name)
    };
  });

  return (
    <div className="min-h-screen bg-white font-body print-content">
      {/* Bouton flottant Télécharger PDF - masqué à l'impression */}
      <div className="fixed top-4 right-4 z-50 print:hidden no-print">
        <button
          onClick={generatePDF}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity shadow-lg"
          style={{ backgroundColor: partnerConfig.colors.primary }}
          data-testid="download-pdf-btn"
        >
          <Download size={18} />
          <span>Télécharger PDF</span>
        </button>
      </div>
      
      {/* Page 1 - Header et Informations */}
      <div className="max-w-[1000px] mx-auto px-4 py-4 sm:px-6 sm:py-6 md:px-8 md:py-8">
        {/* Header avec logo */}
        <div className="flex items-center justify-between mb-6">
          <img 
            src={partnerConfig.logo} 
            alt={partnerConfig.name} 
            className="h-16 sm:h-20 md:h-24"
          />
          <div className="text-right text-sm text-gray-600">
            <div className="font-semibold" style={{ color: partnerConfig.colors.primary }}>Document strictement confidentiel</div>
          </div>
        </div>

        {/* Section flamme avec lignes (avec ou sans icône selon le partenaire) */}
        <div className="relative py-8 mb-8" style={{ background: 'linear-gradient(to bottom, #f9fafb 0%, #f3f4f6 50%, #f9fafb 100%)' }}>
          {/* Ligne de couleur du partenaire */}
          <div className="absolute left-0 right-0 h-0.5" style={{ backgroundColor: partnerConfig.colors.primary, top: '50%', transform: 'translateY(-50%)' }}></div>
          
          {/* Icône flamme au centre (uniquement pour Place des Énergies) */}
          {offer.partner === 'place_des_energies' && (
            <div className="relative flex justify-center">
              <div className="bg-white px-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_offerviewer/artifacts/47sl8bsf_image.png" 
                  alt="Icône flamme"
                  className="h-16 w-auto"
                />
              </div>
            </div>
          )}
        </div>

        {/* Conteneur principal */}
        <div className="mb-8">

          {/* Dates de validité */}
          <div className="flex justify-center gap-8 text-sm mb-8">
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Date de remise :</span>
              <span className="font-semibold">{new Date(offer.created_at).toLocaleDateString('fr-FR')}</span>
              <InfoIcon text="Date à laquelle l'offre a été générée" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Date de validité :</span>
              <span className="font-semibold">
                {new Date(new Date(offer.created_at).setMonth(new Date(offer.created_at).getMonth() + 1)).toLocaleDateString('fr-FR')}
              </span>
              <InfoIcon text="Date jusqu'à laquelle cette offre reste valable" />
            </div>
          </div>

          {/* Titre principal */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4" data-testid="offer-title" style={{ color: '#333' }}>
              Appel d'offres de fourniture<br className="hidden sm:inline"/>
              <span className="sm:hidden"> </span>{offer.energy_type === 'electricity' ? "d'électricité" : 'de gaz'}
            </h1>
            <p className="text-gray-600 mb-6">
              Basé sur votre consommation annuelle de référence (Source: {offer.energy_type === 'electricity' ? 'ENEDIS' : 'GRDF'})
            </p>
          </div>

          {/* Message de bienvenue personnalisé */}
          <div className="bg-blue-50 p-8 mb-8 rounded-lg page-break-avoid">
            <p className="text-gray-700 mb-2">
              <span className="font-semibold">Bonjour{offer.client_info?.name ? ` ${offer.client_info.name}` : ''},</span>
            </p>
            <p className="text-gray-700 mb-2">
              Veuillez trouver ci-dessous le résultat de votre appel d'offres.
            </p>
            <p className="text-gray-700 mb-6">
              Je reste disponible pour toute question ou complément d'information.
            </p>
            <p className="text-gray-700">
              Bien cordialement
            </p>
          </div>
        </div>

        {/* Section Votre contact et Votre entreprise en deux colonnes */}
        <div className="mb-8 page-break-avoid">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 relative">
            {/* Ligne verticale de séparation */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-blue-400 transform -translate-x-1/2"></div>
            
            {/* Colonne gauche - Votre contact */}
            <div>
              <h2 className="text-lg sm:text-xl font-bold mb-6" style={{ color: partnerConfig.colors.primary }}>Votre contact</h2>
              <div className="space-y-3">
                <div>
                  <span className="text-gray-600">Nom : </span>
                  <span className="text-gray-900">{offer.advisor_info?.name || 'Votre conseiller'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Email : </span>
                  <span className="text-gray-900">{offer.advisor_info?.email || partnerConfig.contact.email}</span>
                </div>
                <div>
                  <span className="text-gray-600">Téléphone : </span>
                  <span className="text-gray-900">{offer.advisor_info?.phone || partnerConfig.contact.phone}</span>
                </div>
              </div>
            </div>

            {/* Colonne droite - Votre entreprise */}
            <div>
              <h2 className="text-lg sm:text-xl font-bold mb-6" style={{ color: partnerConfig.colors.primary }}>Votre entreprise</h2>
              <div className="space-y-3" data-testid="client-info-section">
                <div>
                  <span className="text-gray-600">Site : </span>
                  <span className="text-gray-900">{offer.client_info.site_name}</span>
                </div>
                <div>
                  <span className="text-gray-600">Interlocuteur : </span>
                  <span className="text-gray-900">{offer.client_info?.name || '-'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Données contractuelles actuelles */}
        <div className="mb-12 page-break-avoid">
          {/* Ligne bleue épaisse au-dessus du titre */}
          <div className="h-1 mb-6" style={{ backgroundColor: partnerConfig.colors.primary }}></div>
          
          <h2 className="text-2xl font-bold mb-8 text-black">
            Vos données contractuelles actuelles
          </h2>
          
          {/* Tableau à deux colonnes avec lignes alternées */}
          <div className="border border-gray-200">
            {/* Point de livraison */}
            <div className="grid grid-cols-2 border-b border-gray-200">
              <div className="p-5 bg-gray-50 uppercase text-sm text-gray-600 text-center">
                {offer.energy_type === 'electricity' ? 'POINT DE LIVRAISON (RAE/PRM)' : 'POINT DE LIVRAISON (PCE)'}
              </div>
              <div className="p-5 bg-white text-gray-900 text-center">
                {offer.energy_type === 'electricity' 
                  ? (offer.client_info.rae_number || 'Non fourni')
                  : (offer.client_info.pce_number || 'Non fourni')
                }
              </div>
            </div>
            
            {/* Puissance souscrite - uniquement pour électricité */}
            {offer.energy_type === 'electricity' && (
              <div className="grid grid-cols-2 border-b border-gray-200">
                <div className="p-5 bg-white uppercase text-sm text-gray-600 text-center">PUISSANCE SOUSCRITE</div>
                <div className="p-5 bg-gray-50 text-gray-900 text-center">{offer.client_info.subscribed_power ? `${offer.client_info.subscribed_power} kVA` : 'Non fourni'}</div>
              </div>
            )}
            
            {/* Adresse postale */}
            <div className="grid grid-cols-2 border-b border-gray-200">
              <div className="p-5 bg-gray-50 uppercase text-sm text-gray-600 text-center">ADRESSE POSTALE</div>
              <div className="p-5 bg-white text-gray-900 text-center">{offer.client_info.address || 'Non fourni'}</div>
            </div>
            
            {/* Option tarifaire */}
            <div className="grid grid-cols-2 border-b border-gray-200">
              <div className="p-5 bg-white uppercase text-sm text-gray-600 text-center">OPTION TARIFAIRE</div>
              <div className="p-5 bg-gray-50 text-gray-900 text-center">{offer.client_info.tariff || 'Non fourni'}</div>
            </div>
            
            {/* Profil du compteur */}
            <div className="grid grid-cols-2 border-b border-gray-200">
              <div className="p-5 bg-gray-50 uppercase text-sm text-gray-600 text-center">PROFIL DU COMPTEUR</div>
              <div className="p-5 bg-white text-gray-900 text-center">{offer.client_info.profile || 'Non fourni'}</div>
            </div>
            
            {/* Fournisseur actuel */}
            <div className="grid grid-cols-2 border-b border-gray-200">
              <div className="p-5 bg-white uppercase text-sm text-gray-600 text-center">FOURNISSEUR ACTUEL</div>
              <div className="p-5 bg-gray-50 text-gray-900 text-center">{offer.client_info.current_supplier || 'Non fourni'}</div>
            </div>
            
            {/* Tarification du contrat */}
            <div className="grid grid-cols-2 border-b border-gray-200">
              <div className="p-5 bg-gray-50 uppercase text-sm text-gray-600 text-center">TARIFICATION DE VOTRE CONTRAT</div>
              <div className="p-5 bg-white text-gray-900 text-center">{offer.client_info.contract_type || 'Non fourni'}</div>
            </div>
            
            {/* Échéance */}
            <div className="grid grid-cols-2">
              <div className="p-5 bg-white uppercase text-sm text-gray-600 text-center">ÉCHÉANCE</div>
              <div className="p-5 bg-gray-50 text-gray-900 text-center">{offer.client_info.contract_end_date || 'Non fourni'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Page 2 - Tarifs actuels */}
      <div className="max-w-[1000px] mx-auto px-4 py-4 sm:px-6 sm:py-6 md:px-8 md:py-8 print-new-page">
        <h2 className="text-2xl font-bold mb-6 pb-2 border-b-2" style={{ color: partnerConfig.colors.primary, borderColor: partnerConfig.colors.primary }}>
          Vos tarifs actuels
        </h2>

        {/* Cartes de résumé - Pour électricité: 3 cartes (pas de TTC), pour gaz: 4 cartes */}
        <div className={`grid gap-4 mb-8 page-break-avoid ${offer.energy_type === 'electricity' ? 'grid-cols-3' : 'grid-cols-4'}`}>
          <div className="p-4 bg-gray-50 rounded text-center">
            <FileText className="mx-auto mb-2" style={{ color: partnerConfig.colors.primary }} size={32} />
            <div className="text-2xl font-bold">1</div>
            <div className="text-sm text-gray-600">Nombre de sites</div>
          </div>
          <div className="p-4 bg-gray-50 rounded text-center">
            <TrendingUp className="mx-auto mb-2" style={{ color: partnerConfig.colors.primary }} size={32} />
            <div className="text-2xl font-bold">
              {new Intl.NumberFormat('fr-FR', { useGrouping: true }).format(offer.client_info.consumption).replace(/\u202F/g, ' ')}
            </div>
            <div className="text-sm text-gray-600">Consommation annuelle (kWh)</div>
          </div>
          <div className="p-4 bg-gray-50 rounded text-center">
            <div className="mx-auto mb-2 flex items-center justify-center" style={{ width: '32px', height: '32px' }}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '32px', height: '32px' }}>
                <circle cx="12" cy="12" r="10" stroke={partnerConfig.colors.primary} strokeWidth="1.5" fill="none"/>
                <text x="12" y="17" textAnchor="middle" fill={partnerConfig.colors.primary} fontSize="14" fontWeight="bold" fontFamily="Arial">€</text>
              </svg>
            </div>
            <div className="text-2xl font-bold">
              {formatPrice(offer.client_info?.current_budget_detail?.total_ht || budgetActuel / 1.2)} €
            </div>
            <div className="text-sm text-gray-600">Budget total HT</div>
          </div>
          {/* Carte TTC - uniquement pour le gaz */}
          {offer.energy_type !== 'electricity' && (
            <div className="p-4 bg-gray-50 rounded text-center">
              <div className="mx-auto mb-2 flex items-center justify-center" style={{ width: '32px', height: '32px' }}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '32px', height: '32px' }}>
                  <circle cx="12" cy="12" r="10" stroke={partnerConfig.colors.primary} strokeWidth="1.5" fill="none"/>
                  <text x="9" y="17" textAnchor="middle" fill={partnerConfig.colors.primary} fontSize="11" fontWeight="bold" fontFamily="Arial">€</text>
                  <text x="15" y="17" textAnchor="middle" fill={partnerConfig.colors.primary} fontSize="11" fontWeight="bold" fontFamily="Arial">€</text>
                </svg>
              </div>
              <div className="text-2xl font-bold">{formatPrice(offer.client_info?.current_budget_detail?.total_ttc || budgetActuel)} €</div>
              <div className="text-sm text-gray-600">Budget total TTC</div>
            </div>
          )}
        </div>

        {/* Détail des prix */}
        <div className="page-break-avoid">
        <h3 className="text-lg font-bold mb-4">Détail de vos prix</h3>
        <table className="w-full text-sm mb-8">
          <thead className="bg-gray-100">
            <tr>
              <th className="text-left p-3">Postes</th>
              <th className="text-right p-3">Prix (€/MWh)</th>
              <th className="text-right p-3">Budget annuel (€)</th>
            </tr>
          </thead>
          <tbody>
            {offer.client_info?.current_budget_detail ? (
              offer.energy_type === 'electricity' ? (
                /* TABLEAU ÉLECTRICITÉ */
                <>
                  <tr className="border-b bg-white">
                    <td className="p-3 font-semibold">HPH (Heures Pleines Hiver)</td>
                    <td className="p-3 text-right">-</td>
                    <td className="p-3 text-right">{formatPrice(offer.client_info.current_budget_detail.electricity_budgets?.hph || 0)}</td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="p-3 font-semibold">HCH (Heures Creuses Hiver)</td>
                    <td className="p-3 text-right">-</td>
                    <td className="p-3 text-right">{formatPrice(offer.client_info.current_budget_detail.electricity_budgets?.hch || 0)}</td>
                  </tr>
                  <tr className="border-b bg-white">
                    <td className="p-3 font-semibold">HPE (Heures Pleines Été)</td>
                    <td className="p-3 text-right">-</td>
                    <td className="p-3 text-right">{formatPrice(offer.client_info.current_budget_detail.electricity_budgets?.hpe || 0)}</td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="p-3 font-semibold">HCE (Heures Creuses Été)</td>
                    <td className="p-3 text-right">-</td>
                    <td className="p-3 text-right">{formatPrice(offer.client_info.current_budget_detail.electricity_budgets?.hce || 0)}</td>
                  </tr>
                  {(offer.client_info.electricity_consumption?.pte > 0 || offer.client_info.current_budget_detail.electricity_budgets?.pte > 0) && (
                    <tr className="border-b bg-white">
                      <td className="p-3 font-semibold">PTE (Pointe)</td>
                      <td className="p-3 text-right">-</td>
                      <td className="p-3 text-right">{formatPrice(offer.client_info.current_budget_detail.electricity_budgets?.pte || 0)}</td>
                    </tr>
                  )}
                  <tr className="border-b bg-gray-50">
                    <td className="p-3 font-semibold">Abonnement</td>
                    <td className="p-3 text-right">{formatPrice(offer.client_info.current_budget_detail.subscription_price)} €/mois</td>
                    <td className="p-3 text-right">{formatPrice(offer.client_info.current_budget_detail.subscription_price * 12)}</td>
                  </tr>
                  <tr className="border-b-2 border-gray-300 font-bold" style={{ backgroundColor: '#E8F4F8' }}>
                    <td className="p-3">TOTAL FOURNITURE</td>
                    <td className="p-3 text-right"></td>
                    <td className="p-3 text-right">{formatPrice(offer.client_info.current_budget_detail.total_supply_ht)}</td>
                  </tr>
                  <tr className="border-b bg-white">
                    <td className="p-3">TURPE (Acheminement)</td>
                    <td className="p-3 text-right">-</td>
                    <td className="p-3 text-right">{formatPrice(offer.client_info.current_budget_detail.turpe_budget)}</td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="p-3">CTA</td>
                    <td className="p-3 text-right">-</td>
                    <td className="p-3 text-right">{formatPrice(offer.client_info.current_budget_detail.cta_tax)}</td>
                  </tr>
                  <tr className="border-b bg-white">
                    <td className="p-3">CSPE</td>
                    <td className="p-3 text-right">-</td>
                    <td className="p-3 text-right">{formatPrice(offer.client_info.current_budget_detail.cspe_budget)}</td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="p-3">Capacité</td>
                    <td className="p-3 text-right">-</td>
                    <td className="p-3 text-right">{formatPrice(offer.client_info.current_budget_detail.capacity_budget)}</td>
                  </tr>
                  <tr className="border-b-2 border-gray-300 font-bold" style={{ backgroundColor: '#E8F4F8' }}>
                    <td className="p-3">TOTAL TAXES & ACHEMINEMENT</td>
                    <td className="p-3 text-right"></td>
                    <td className="p-3 text-right">{formatPrice(offer.client_info.current_budget_detail.total_taxes)}</td>
                  </tr>
                  {/* Pour électricité: TOTAL HT est la ligne finale (pas de TVA/TTC) */}
                  <tr className="font-bold text-lg" style={{ backgroundColor: partnerConfig.colors.primary, color: 'white' }}>
                    <td className="p-3">TOTAL HT</td>
                    <td className="p-3 text-right"></td>
                    <td className="p-3 text-right">{formatPrice(offer.client_info.current_budget_detail.total_ht)}</td>
                  </tr>
                </>
              ) : (
                /* TABLEAU GAZ */
                <>
                  <tr className="border-b bg-white">
                    <td className="p-3 font-semibold">Molécule</td>
                    <td className="p-3 text-right ">{formatPrice(offer.client_info.current_budget_detail.molecule_price)}</td>
                    <td className="p-3 text-right ">{formatPrice(offer.client_info.current_budget_detail.molecule_price * (offer.client_info.consumption / 1000))}</td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="p-3 font-semibold">Terme variable</td>
                    <td className="p-3 text-right ">{formatPrice(offer.client_info.current_budget_detail.variable_term_price)}</td>
                    <td className="p-3 text-right ">{formatPrice(offer.client_info.current_budget_detail.variable_term_price * (offer.client_info.consumption / 1000))}</td>
                  </tr>
                  <tr className="border-b bg-white">
                    <td className="p-3 font-semibold">Abonnement</td>
                    <td className="p-3 text-right ">{formatPrice(offer.client_info.current_budget_detail.subscription_price)} €/mois</td>
                    <td className="p-3 text-right ">{formatPrice(offer.client_info.current_budget_detail.subscription_price * 12)}</td>
                  </tr>
                  {/* CPB - Contribution au Plafonnement Biométhane (si présent) */}
                  {offer.client_info.current_budget_detail.cpb_budget > 0 && (
                    <tr className="border-b bg-gray-50">
                      <td className="p-3 font-semibold">CPB</td>
                      <td className="p-3 text-right ">{formatPrice(offer.client_info.current_budget_detail.cpb_price)}</td>
                      <td className="p-3 text-right ">{formatPrice(offer.client_info.current_budget_detail.cpb_budget)}</td>
                    </tr>
                  )}
                  <tr className="border-b-2 border-gray-300 font-bold" style={{ backgroundColor: '#E8F4F8' }}>
                    <td className="p-3">TOTAL FOURNITURE</td>
                    <td className="p-3 text-right"></td>
                    <td className="p-3 text-right ">{formatPrice(offer.client_info.current_budget_detail.total_supply_ht)}</td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="p-3">CEE</td>
                    <td className="p-3 text-right">
                      {offer.client_info.current_budget_detail.cee_price > 0 
                        ? formatPrice(offer.client_info.current_budget_detail.cee_price)
                        : '-'}
                    </td>
                    <td className="p-3 text-right ">
                      {offer.client_info.current_budget_detail.cee_budget > 0 
                        ? formatPrice(offer.client_info.current_budget_detail.cee_budget)
                        : '-'}
                    </td>
                  </tr>
                  <tr className="border-b bg-white">
                    <td className="p-3">CTA</td>
                    <td className="p-3 text-right ">
                      {formatPrice((offer.client_info.current_budget_detail.cta_tax || 0) / 12)}
                    </td>
                    <td className="p-3 text-right ">{formatPrice(offer.client_info.current_budget_detail.cta_tax)}</td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="p-3">TICGN</td>
                    <td className="p-3 text-right ">
                      {formatPrice((offer.client_info.current_budget_detail.ticgn_tax || 0) / (offer.client_info.consumption / 1000))}
                    </td>
                    <td className="p-3 text-right ">{formatPrice(offer.client_info.current_budget_detail.ticgn_tax)}</td>
                  </tr>
                  <tr className="border-b-2 border-gray-300 font-bold" style={{ backgroundColor: '#E8F4F8' }}>
                    <td className="p-3">TOTAL TAXES</td>
                    <td className="p-3 text-right"></td>
                    <td className="p-3 text-right ">{formatPrice(offer.client_info.current_budget_detail.total_taxes)}</td>
                  </tr>
                  <tr className="border-b font-bold bg-white">
                    <td className="p-3">TOTAL HT</td>
                    <td className="p-3 text-right"></td>
                    <td className="p-3 text-right ">{formatPrice(offer.client_info.current_budget_detail.total_ht)}</td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="p-3">TVA 20%</td>
                    <td className="p-3 text-right"></td>
                    <td className="p-3 text-right ">{formatPrice(offer.client_info.current_budget_detail.tva)}</td>
                  </tr>
                  <tr className="font-bold text-lg" style={{ backgroundColor: partnerConfig.colors.primary, color: 'white' }}>
                    <td className="p-3">TOTAL TTC</td>
                    <td className="p-3 text-right"></td>
                    <td className="p-3 text-right ">{formatPrice(offer.client_info.current_budget_detail.total_ttc)}</td>
                  </tr>
                </>
              )
            ) : (
              <tr>
                <td colSpan="3" className="p-3 text-center text-gray-500">Détails du budget actuel non disponibles</td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Page 3 - Podium des offres avec graphique */}
      <div className="max-w-[1000px] mx-auto px-4 py-4 sm:px-6 sm:py-6 md:px-8 md:py-8 print-new-page" data-testid="comparison-table">
        <div className="flex items-center justify-between mb-6 page-break-avoid">
          <div>
            <h2 className="text-2xl font-bold pb-2 border-b-2" style={{ color: partnerConfig.colors.primary, borderColor: partnerConfig.colors.primary }}>
              Podium des offres retenues
            </h2>
            <div className="text-sm text-gray-600 mt-2">
              Offres valables jusqu'au: {new Date().toLocaleDateString('fr-FR')}
            </div>
          </div>
          
          {/* Toggle HT/TTC - masqué pour l'électricité (toujours HT) */}
          {offer.energy_type !== 'electricity' && (
            <div className="flex gap-2 items-center pdf-hidden">
              <button
                onClick={() => setShowTTC(false)}
                className={`px-4 py-2 rounded-sm font-semibold transition-all ${
                  !showTTC 
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={!showTTC ? { backgroundColor: partnerConfig.colors.primary } : {}}
                data-testid="toggle-ht"
              >
                HT
              </button>
              <button
                onClick={() => setShowTTC(true)}
                className={`px-4 py-2 rounded-sm font-semibold transition-all ${
                  showTTC 
                    ? 'text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={showTTC ? { backgroundColor: partnerConfig.colors.primary } : {}}
                data-testid="toggle-ttc"
              >
                TTC
              </button>
            </div>
          )}
        </div>

        {/* Budget actuel */}
        <div className="mb-6 p-4 bg-gray-50 rounded">
          <div className="text-sm text-gray-600">Budget actuel annuel</div>
          <div className="text-2xl sm:text-3xl font-bold" style={{ color: partnerConfig.colors.primary }}>
            {offer.energy_type === 'electricity' 
              ? `${formatPrice(offer.client_info?.current_budget_detail?.total_ht || budgetActuel / 1.2)} € HT`
              : `${formatPrice(showTTC ? budgetActuel : budgetActuel / 1.2)} € ${showTTC ? 'TTC' : 'HT'}`
            }
          </div>
        </div>

        {/* Graphique en barres empilées - Version CSS */}
        <div className="mb-8 bg-white p-6 rounded shadow-sm border border-gray-200">
          <div className="mb-6 flex gap-6 justify-center text-sm flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: partnerConfig.colors.primary }}></div>
              <span className="flex items-center">
                Fourniture
                <InfoIcon text="Part molécule : Prix de la fourniture d'énergie" />
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#FFA500' }}></div>
              <span className="flex items-center">
                Acheminement
                <InfoIcon text="Acheminement : Terme variable + Abonnement mensuel comprenant ATRT / ATRD / Stockage / Part Fournisseur" />
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#687175' }}></div>
              <span className="flex items-center">
                Taxes et contributions
                <InfoIcon text="Taxes et contributions (sans TVA) : CEE + CTA + TICGN" />
              </span>
            </div>
            {/* Légende TVA - masquée pour l'électricité */}
            {offer.energy_type !== 'electricity' && showTTC && (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#9CA3AF' }}></div>
                <span className="flex items-center">
                  TVA 20%
                  <InfoIcon text="Taxe sur la Valeur Ajoutée : 20% du total HT" />
                </span>
              </div>
            )}
          </div>
          
          {/* Graphique CSS */}
          {(() => {
            // Calculer le maximum dynamiquement basé sur les données réelles
            const isElectricity = offer.energy_type === 'electricity';
            const useHT = isElectricity || !showTTC;
            
            // Trouver la valeur max parmi tous les fournisseurs
            const maxSupplierValue = Math.max(...validSuppliers.map(s => 
              useHT ? (s.total_ht || (s.total_ttc / 1.2)) : s.total_ttc
            ));
            
            // Arrondir au multiple de 10000 supérieur pour un axe propre
            const roundedMax = Math.ceil(maxSupplierValue / 10000) * 10000;
            // S'assurer d'avoir au moins 20000 et un peu de marge (10% de plus)
            const chartMaxValue = Math.max(roundedMax * 1.1, 20000);
            
            // Générer les labels de l'axe Y (5 niveaux)
            const yAxisLabels = [
              chartMaxValue,
              chartMaxValue * 0.75,
              chartMaxValue * 0.5,
              chartMaxValue * 0.25,
              0
            ];
            
            return (
          <>
          <div className="relative" style={{ height: '400px' }}>
            {/* Axes - labels dynamiques */}
            <div className="absolute left-0 top-0 bottom-12 flex flex-col justify-between text-xs text-gray-600" style={{ width: '50px' }}>
              {yAxisLabels.map((value, i) => (
                <div key={i} className="text-right pr-2">
                  {value >= 1000 ? `${Math.round(value / 1000)} 000 €` : `${Math.round(value)} €`}
                </div>
              ))}
            </div>
            
            {/* Grid lines */}
            <div className="absolute left-12 right-0 top-0 bottom-12 flex flex-col justify-between">
              <div className="border-t border-gray-200"></div>
              <div className="border-t border-gray-200"></div>
              <div className="border-t border-gray-200"></div>
              <div className="border-t border-gray-200"></div>
              <div className="border-t border-gray-300"></div>
            </div>
            
            {/* Barres */}
            <div className="absolute left-12 right-0 top-0 bottom-12 flex items-end justify-around gap-4 px-12">
              {chartData.map((item, index) => {
                // Pour l'électricité: toujours HT, pour le gaz: selon le toggle
                const supplier = validSuppliers[index];
                const supplierTotal = useHT 
                  ? (supplier.total_ht || (supplier.total_ttc / 1.2))
                  : supplier.total_ttc;
                
                const totalHeight = (supplierTotal / chartMaxValue) * 100;
                
                // Calculer les pourcentages pour chaque section
                const fournitureValue = item.Fourniture;
                const acheminementValue = item.Acheminement;
                const taxesValue = item.Taxes; // CEE + CTA + TICGN (sans TVA)
                // TVA uniquement pour le gaz en mode TTC
                const tvaValue = (!isElectricity && showTTC) ? (supplier.tva || 0) : 0;
                
                // Les pourcentages sont calculés sur le total (HT ou TTC selon le mode)
                const fourniturePercentage = (fournitureValue / supplierTotal) * 100;
                const acheminementPercentage = (acheminementValue / supplierTotal) * 100;
                const taxesPercentage = (taxesValue / supplierTotal) * 100; // Toujours visible
                const tvaPercentage = tvaValue > 0 ? (tvaValue / supplierTotal) * 100 : 0;
                
                return (
                  <div key={index} className="flex-1 flex flex-col items-center justify-end" style={{ height: '100%' }}>
                    {/* Barre empilée (3 sections) */}
                    <div 
                      className="w-full max-w-[120px] relative flex flex-col-reverse" 
                      style={{ height: `${totalHeight}%`, minHeight: '20px' }}
                    >
                      {/* Fourniture (bas) - Molécule uniquement */}
                      <div 
                        className="w-full transition-all duration-300 cursor-pointer hover:opacity-90"
                        style={{ 
                          backgroundColor: partnerConfig.colors.primary,
                          height: `${fourniturePercentage}%`,
                          minHeight: '10px'
                        }}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltip({
                            show: true,
                            content: `Fourniture: ${formatPrice(fournitureValue)} €\n${isElectricity ? 'HPH + HCH + HPE + HCE' : 'Molécule uniquement'}`,
                            x: rect.left + rect.width / 2,
                            y: rect.top - 10
                          });
                        }}
                        onMouseLeave={() => setTooltip({ show: false, content: '', x: 0, y: 0 })}
                      ></div>
                      
                      {/* Acheminement (milieu) - Terme variable + Abonnement */}
                      <div 
                        className="w-full transition-all duration-300 cursor-pointer hover:opacity-90"
                        style={{ 
                          backgroundColor: '#FFA500',
                          height: `${acheminementPercentage}%`,
                          minHeight: '10px'
                        }}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltip({
                            show: true,
                            content: `Acheminement: ${formatPrice(acheminementValue)} €\n${isElectricity ? 'TURPE + Abonnement' : 'Terme variable + Abonnement'}`,
                            x: rect.left + rect.width / 2,
                            y: rect.top - 10
                          });
                        }}
                        onMouseLeave={() => setTooltip({ show: false, content: '', x: 0, y: 0 })}
                      ></div>
                      
                      {/* Taxes (CEE + CTA + TICGN) - TOUJOURS visible (HT et TTC) */}
                      <div 
                        className="w-full transition-all duration-300 cursor-pointer hover:opacity-90"
                        style={{ 
                          backgroundColor: '#687175',
                          height: `${taxesPercentage}%`,
                          minHeight: '5px'
                        }}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const taxesDetails = isElectricity 
                            ? `CTA: ${formatPrice(supplier.cta_tax || 0)} € • CSPE: ${formatPrice(supplier.cspe_budget || 0)} €`
                            : `CTA: ${formatPrice(supplier.cta_tax || 0)} € • TICGN: ${formatPrice(supplier.ticgn_tax || 0)} €`;
                          // N'afficher CEE que si non inclus dans molécule et > 0
                          const ceeInfo = (!supplier.cee_included_in_molecule && supplier.cee_budget > 0) 
                            ? ` • CEE: ${formatPrice(supplier.cee_budget || 0)} €` 
                            : (supplier.cee_included_in_molecule ? ' • CEE: inclus' : '');
                          setTooltip({
                            show: true,
                            content: `Taxes et contributions: ${formatPrice(taxesValue)} €\n${taxesDetails}${ceeInfo}`,
                            x: rect.left + rect.width / 2,
                            y: rect.top - 10
                          });
                        }}
                        onMouseLeave={() => setTooltip({ show: false, content: '', x: 0, y: 0 })}
                      ></div>
                      
                      {/* TVA 20% - visible UNIQUEMENT pour le gaz en mode TTC */}
                      {!isElectricity && showTTC && tvaValue > 0 && (
                        <div 
                          className="w-full transition-all duration-300 cursor-pointer hover:opacity-90"
                          style={{ 
                            backgroundColor: '#9CA3AF',
                            height: `${tvaPercentage}%`,
                            minHeight: '3px'
                          }}
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setTooltip({
                              show: true,
                              content: `TVA 20%: ${formatPrice(tvaValue)} €`,
                              x: rect.left + rect.width / 2,
                              y: rect.top - 10
                            });
                          }}
                          onMouseLeave={() => setTooltip({ show: false, content: '', x: 0, y: 0 })}
                        ></div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Tooltip */}
            {tooltip.show && (
              <div 
                className="fixed z-50 px-4 py-3 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-xl pointer-events-none whitespace-pre-line"
                style={{
                  left: `${tooltip.x}px`,
                  top: `${tooltip.y}px`,
                  transform: 'translate(-50%, -100%)',
                  maxWidth: '300px'
                }}
              >
                {tooltip.content}
                {/* Arrow */}
                <div 
                  className="absolute left-1/2 bottom-0 w-0 h-0"
                  style={{
                    transform: 'translate(-50%, 100%)',
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: '6px solid rgb(17, 24, 39)'
                  }}
                ></div>
              </div>
            )}
          </div>
          
          {/* Labels fournisseurs */}
          <div className={`grid gap-4 mt-6 pt-6 border-t border-gray-200 ${chartData.length === 1 ? 'grid-cols-1 max-w-xs mx-auto' : chartData.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {chartData.map((item, index) => (
              <div key={index} className="text-center">
                {item.logo && (
                  <img 
                    src={item.logo} 
                    alt={item.supplier} 
                    className="h-16 w-auto max-w-[120px] mx-auto mb-3 object-contain"
                  />
                )}
                <div className="font-bold mb-1" style={{ color: '#191D1E' }}>{item.name}</div>
                <div className="text-sm mb-2" style={{ color: '#191D1E' }}>{item.supplier}</div>
                <button
                  onClick={() => {
                    setSelectedSupplier(item.supplier);
                    setShowSupplierModal(true);
                  }}
                  className="text-xs px-3 py-1 rounded transition-colors pdf-hidden"
                  style={{ 
                    backgroundColor: partnerConfig.colors.primary,
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = partnerConfig.colors.primary}
                >
                  Voir la fiche
                </button>
              </div>
            ))}
          </div>
          </>
            );
          })()}
        </div>

        {/* Budgets futurs */}
        <div className="page-break-avoid">
        <h3 className="text-lg font-bold mt-8 mb-4">Budgets annuels futurs</h3>
        <div className={`grid gap-6 mb-8 ${validSuppliers.slice(0, 3).length === 1 ? 'grid-cols-1 max-w-sm mx-auto' : validSuppliers.slice(0, 3).length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
          {validSuppliers.slice(0, 3).map((supplier, index) => {
            // Pour l'électricité: toujours HT
            const isElectricity = offer.energy_type === 'electricity';
            const useHT = isElectricity || !showTTC;
            const displayValue = useHT ? (supplier.total_ht || (supplier.total_ttc / 1.2)) : supplier.total_ttc;
            const budgetReference = useHT 
              ? (offer.client_info?.current_budget_detail?.total_ht || budgetActuel / 1.2)
              : budgetActuel;
            
            return (
              <div key={index} className="text-center p-4 bg-gray-50 rounded" data-testid={`supplier-detail-${index}`}>
                <div className="text-sm text-gray-600 mb-2">Budget annuel futur</div>
                <div className="text-2xl font-bold mb-2" style={{ color: partnerConfig.colors.primary }}>
                  {formatPrice(displayValue)} € {isElectricity ? 'HT' : (showTTC ? 'TTC' : 'HT')}
                </div>
                <div className="text-sm">
                  <span className={displayValue < budgetReference ? 'text-green-600' : 'text-red-600'}>
                    {displayValue < budgetReference ? '▼' : '▲'} {formatPrice(Math.abs(displayValue - budgetReference))} €
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        </div>

        {/* Détail des prix pour chaque offre */}
        <div className="print-new-page">
        <h3 className="text-lg font-bold mb-4 mt-8">Détail des prix</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" style={{ fontSize: '14px' }}>
            <thead>
              <tr className="bg-gray-200">
                <th className="text-left p-3 border border-gray-300" style={{ minWidth: '180px', fontWeight: '600' }}>Postes</th>
                {validSuppliers.slice(0, 3).map((supplier, index) => (
                  <th key={index} className="text-center p-3 border border-gray-300 font-semibold">
                    Offre #{index + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {offer.energy_type === 'electricity' ? (
                /* TABLEAU ÉLECTRICITÉ - OFFRES FOURNISSEURS */
                <>
                  {/* HPH */}
                  <tr className="border-b">
                    <td className="p-3 border border-gray-300 font-semibold bg-gray-50">
                      <span className="flex items-center">
                        HPH (Heures Pleines Hiver)
                        <InfoIcon text="Consommation en heures pleines période hivernale" />
                      </span>
                    </td>
                    {validSuppliers.slice(0, 3).map((supplier, index) => (
                      <td key={index} className="p-3 border border-gray-300 text-center">
                        <div className="font-semibold text-base">{formatPrice(applyVAT(supplier.electricity_budgets?.hph || 0))} €</div>
                        <div className="text-xs mt-1" style={{ color: partnerConfig.colors.primary }}>{formatPrice(applyVAT(supplier.electricity_prices?.hph || 0))} €/MWh</div>
                      </td>
                    ))}
                  </tr>
                  {/* HCH */}
                  <tr className="border-b">
                    <td className="p-3 border border-gray-300 font-semibold bg-gray-50">
                      <span className="flex items-center">
                        HCH (Heures Creuses Hiver)
                        <InfoIcon text="Consommation en heures creuses période hivernale" />
                      </span>
                    </td>
                    {validSuppliers.slice(0, 3).map((supplier, index) => (
                      <td key={index} className="p-3 border border-gray-300 text-center">
                        <div className="font-semibold text-base">{formatPrice(applyVAT(supplier.electricity_budgets?.hch || 0))} €</div>
                        <div className="text-xs mt-1" style={{ color: partnerConfig.colors.primary }}>{formatPrice(applyVAT(supplier.electricity_prices?.hch || 0))} €/MWh</div>
                      </td>
                    ))}
                  </tr>
                  {/* HPE */}
                  <tr className="border-b">
                    <td className="p-3 border border-gray-300 font-semibold bg-gray-50">
                      <span className="flex items-center">
                        HPE (Heures Pleines Été)
                        <InfoIcon text="Consommation en heures pleines période estivale" />
                      </span>
                    </td>
                    {validSuppliers.slice(0, 3).map((supplier, index) => (
                      <td key={index} className="p-3 border border-gray-300 text-center">
                        <div className="font-semibold text-base">{formatPrice(applyVAT(supplier.electricity_budgets?.hpe || 0))} €</div>
                        <div className="text-xs mt-1" style={{ color: partnerConfig.colors.primary }}>{formatPrice(applyVAT(supplier.electricity_prices?.hpe || 0))} €/MWh</div>
                      </td>
                    ))}
                  </tr>
                  {/* HCE */}
                  <tr className="border-b">
                    <td className="p-3 border border-gray-300 font-semibold bg-gray-50">
                      <span className="flex items-center">
                        HCE (Heures Creuses Été)
                        <InfoIcon text="Consommation en heures creuses période estivale" />
                      </span>
                    </td>
                    {validSuppliers.slice(0, 3).map((supplier, index) => (
                      <td key={index} className="p-3 border border-gray-300 text-center">
                        <div className="font-semibold text-base">{formatPrice(applyVAT(supplier.electricity_budgets?.hce || 0))} €</div>
                        <div className="text-xs mt-1" style={{ color: partnerConfig.colors.primary }}>{formatPrice(applyVAT(supplier.electricity_prices?.hce || 0))} €/MWh</div>
                      </td>
                    ))}
                  </tr>
                  {/* Abonnement */}
                  <tr className="border-b">
                    <td className="p-3 border border-gray-300 font-semibold bg-gray-50">ABONNEMENT</td>
                    {validSuppliers.slice(0, 3).map((supplier, index) => (
                      <td key={index} className="p-3 border border-gray-300 text-center">
                        <div className="font-semibold text-base">{formatPrice(applyVAT(supplier.subscription_budget_annual || 0))} €</div>
                        <div className="text-xs mt-1" style={{ color: partnerConfig.colors.primary }}>{formatPrice(applyVAT(supplier.subscription_price || 0))} €/Mois</div>
                      </td>
                    ))}
                  </tr>
                  {/* Total Fourniture */}
                  <tr className="border-b" style={{ backgroundColor: '#f0f0f0' }}>
                    <td className="p-3 border border-gray-300 font-bold">TOTAL FOURNITURE</td>
                    {validSuppliers.slice(0, 3).map((supplier, index) => (
                      <td key={index} className="p-3 border border-gray-300 text-center">
                        <div className="font-bold text-base">{formatPrice(supplier.total_supply_ht || 0)} €</div>
                      </td>
                    ))}
                  </tr>
                  {/* TURPE */}
                  <tr className="border-b">
                    <td className="p-3 border border-gray-300 font-semibold bg-gray-50">
                      <span className="flex items-center">
                        TURPE
                        <InfoIcon text="Tarif d'Utilisation des Réseaux Publics d'Électricité" />
                      </span>
                    </td>
                    {validSuppliers.slice(0, 3).map((supplier, index) => (
                      <td key={index} className="p-3 border border-gray-300 text-center">
                        <div className="font-semibold text-base">{formatPrice(supplier.turpe_budget || 0)} €</div>
                      </td>
                    ))}
                  </tr>
                  {/* CTA */}
                  <tr className="border-b">
                    <td className="p-3 border border-gray-300 font-semibold bg-gray-50">
                      <span className="flex items-center">
                        CTA
                        <InfoIcon text="Contribution Tarifaire d'Acheminement" />
                      </span>
                    </td>
                    {validSuppliers.slice(0, 3).map((supplier, index) => (
                      <td key={index} className="p-3 border border-gray-300 text-center">
                        <div className="font-semibold text-base">{formatPrice(supplier.cta_tax || 0)} €</div>
                      </td>
                    ))}
                  </tr>
                  {/* CSPE */}
                  <tr className="border-b">
                    <td className="p-3 border border-gray-300 font-semibold bg-gray-50">
                      <span className="flex items-center">
                        CSPE
                        <InfoIcon text="Contribution au Service Public de l'Électricité" />
                      </span>
                    </td>
                    {validSuppliers.slice(0, 3).map((supplier, index) => (
                      <td key={index} className="p-3 border border-gray-300 text-center">
                        <div className="font-semibold text-base">{formatPrice(supplier.cspe_budget || 0)} €</div>
                      </td>
                    ))}
                  </tr>
                  {/* Capacité */}
                  <tr className="border-b">
                    <td className="p-3 border border-gray-300 font-semibold bg-gray-50">
                      <span className="flex items-center">
                        CAPACITÉ
                        <InfoIcon text="Marché de capacité pour la sécurité d'approvisionnement" />
                      </span>
                    </td>
                    {validSuppliers.slice(0, 3).map((supplier, index) => (
                      <td key={index} className="p-3 border border-gray-300 text-center">
                        <div className="font-semibold text-base">{formatPrice(supplier.capacity_budget || 0)} €</div>
                      </td>
                    ))}
                  </tr>
                  {/* Total Taxes */}
                  <tr className="border-b" style={{ backgroundColor: '#f0f0f0' }}>
                    <td className="p-3 border border-gray-300 font-bold">TOTAL TAXES & ACHEMINEMENT</td>
                    {validSuppliers.slice(0, 3).map((supplier, index) => (
                      <td key={index} className="p-3 border border-gray-300 text-center">
                        <div className="font-bold text-base">{formatPrice(supplier.total_taxes || 0)} €</div>
                      </td>
                    ))}
                  </tr>
                  {/* Total HT - Ligne finale pour l'électricité */}
                  <tr style={{ backgroundColor: partnerConfig.colors.primary }}>
                    <td className="p-3 border border-gray-300 font-bold text-white">TOTAL HT</td>
                    {validSuppliers.slice(0, 3).map((supplier, index) => (
                      <td key={index} className="p-3 border border-gray-300 text-center">
                        <div className="font-bold text-lg text-white">{formatPrice(supplier.total_ht || 0)} €</div>
                      </td>
                    ))}
                  </tr>
                  {/* Évolution */}
                  <tr className="border-b">
                    <td className="p-3 border border-gray-300 font-semibold bg-green-50">ÉVOLUTION</td>
                    {validSuppliers.slice(0, 3).map((supplier, index) => (
                      <td key={index} className="p-3 border border-gray-300 text-center">
                        <div className={`font-bold text-base ${(supplier.evolution_percentage || 0) < 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {supplier.evolution_percentage ? `${supplier.evolution_percentage > 0 ? '+' : ''}${supplier.evolution_percentage.toFixed(1)}%` : '-'}
                        </div>
                        {supplier.evolution_amount && (
                          <div className="text-xs mt-1 text-green-600">
                            {supplier.evolution_amount > 0 ? '-' : '+'}{formatPrice(Math.abs(supplier.evolution_amount))} €
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                </>
              ) : (
                /* TABLEAU GAZ - OFFRES FOURNISSEURS */
                <>
              {/* Molécule */}
              <tr className="border-b">
                <td className="p-3 border border-gray-300 font-semibold bg-gray-50">
                  <span className="flex items-center">
                    MOLÉCULE
                    <InfoIcon text="Prix de la fourniture d'énergie" />
                  </span>
                </td>
                {validSuppliers.slice(0, 3).map((supplier, index) => (
                  <td key={index} className="p-3 border border-gray-300 text-center">
                    <div className="font-semibold text-base">{formatPrice(applyVAT(supplier.molecule_budget_annual || 0))} €</div>
                    <div className="text-xs mt-1" style={{ color: partnerConfig.colors.primary }}>{formatPrice(applyVAT(supplier.molecule_price || 0))} €/MWh</div>
                  </td>
                ))}
              </tr>
              
              {/* Terme variable */}
              <tr className="border-b">
                <td className="p-3 border border-gray-300 font-semibold bg-gray-50">
                  <span className="flex items-center">
                    TERME VARIABLE
                    <InfoIcon text="Coût variable du transport de l'énergie (ATRT / ATRD)" />
                  </span>
                </td>
                {validSuppliers.slice(0, 3).map((supplier, index) => (
                  <td key={index} className="p-3 border border-gray-300 text-center">
                    <div className="font-semibold text-base">{formatPrice(applyVAT(supplier.variable_term_budget_annual || 0))} €</div>
                    <div className="text-xs mt-1" style={{ color: partnerConfig.colors.primary }}>{formatPrice(applyVAT(supplier.variable_term_price || 0))} €/MWh</div>
                  </td>
                ))}
              </tr>
              
              {/* Abonnement */}
              <tr className="border-b">
                <td className="p-3 border border-gray-300 font-semibold bg-gray-50">
                  <span className="flex items-center">
                    ABONNEMENT
                    <InfoIcon text="Abonnement mensuel comprenant ATRT / ATRD / Stockage / Part Fournisseur" />
                  </span>
                </td>
                {validSuppliers.slice(0, 3).map((supplier, index) => (
                  <td key={index} className="p-3 border border-gray-300 text-center">
                    <div className="font-semibold text-base">{formatPrice(applyVAT(supplier.subscription_budget_annual || 0))} €</div>
                    <div className="text-xs mt-1" style={{ color: partnerConfig.colors.primary }}>{formatPrice(applyVAT(supplier.subscription_price || 0))} €/Mois</div>
                  </td>
                ))}
              </tr>
              
              {/* Option verte */}
              <tr className="border-b">
                <td className="p-3 border border-gray-300 font-semibold bg-gray-50">OPTION VERTE</td>
                {validSuppliers.slice(0, 3).map((supplier, index) => (
                  <td key={index} className="p-3 border border-gray-300 text-center">
                    <div className="font-semibold text-base">
                      {supplier.green_option_budget_annual ? `${formatPrice(applyVAT(supplier.green_option_budget_annual || 0))} €` : '0 €'}
                    </div>
                    {supplier.green_option_price > 0 && (
                      <div className="text-xs mt-1" style={{ color: partnerConfig.colors.primary }}>{formatPrice(applyVAT(supplier.green_option_price))} €</div>
                    )}
                  </td>
                ))}
              </tr>
              
              {/* Total Fourniture */}
              <tr className="border-b" style={{ backgroundColor: '#f0f0f0' }}>
                <td className="p-3 border border-gray-300 font-bold">TOTAL FOURNITURE</td>
                {validSuppliers.slice(0, 3).map((supplier, index) => (
                  <td key={index} className="p-3 border border-gray-300 text-center">
                    <div className="font-bold text-base">{formatPrice(supplier.total_supply_ht || 0)} €</div>
                    <div className="text-xs mt-1" style={{ color: partnerConfig.colors.primary }}>
                      {formatPrice((supplier.total_supply_ht || 0) / (offer.client_info.consumption / 1000))} €
                    </div>
                  </td>
                ))}
              </tr>
              
              {/* CEE - Afficher seulement si cee_included_in_molecule est false ET cee_budget > 0 */}
              <tr className="border-b">
                <td className="p-3 border border-gray-300 font-semibold bg-gray-50">
                  <span className="flex items-center">
                    CEE
                    <InfoIcon text="Certificats d'Économies d'Énergie : Obligation réglementaire pour les fournisseurs d'énergie" />
                  </span>
                </td>
                {validSuppliers.slice(0, 3).map((supplier, index) => (
                  <td key={index} className="p-3 border border-gray-300 text-center">
                    {supplier.cee_included_in_molecule ? (
                      <div className="font-semibold text-base text-gray-400" title="CEE inclus dans le prix molécule">
                        Inclus
                      </div>
                    ) : supplier.cee_budget > 0 ? (
                      <>
                        <div className="font-semibold text-base">{formatPrice(applyVAT(supplier.cee_budget || 0))} €</div>
                        <div className="text-xs mt-1" style={{ color: partnerConfig.colors.primary }}>
                          {formatPrice(supplier.cee_budget / (offer.client_info.consumption / 1000))} €/MWh
                        </div>
                      </>
                    ) : (
                      <div className="font-semibold text-base">-</div>
                    )}
                  </td>
                ))}
              </tr>
              
              {/* CTA */}
              <tr className="border-b">
                <td className="p-3 border border-gray-300 font-semibold bg-gray-50">
                  <span className="flex items-center">
                    CTA
                    <InfoIcon text="Contribution Tarifaire d'Acheminement : Taxe finançant le tarif social de solidarité" />
                  </span>
                </td>
                {validSuppliers.slice(0, 3).map((supplier, index) => (
                  <td key={index} className="p-3 border border-gray-300 text-center">
                    <div className="font-semibold text-base">{formatPrice(supplier.cta_tax || 0)} €</div>
                    <div className="text-xs mt-1" style={{ color: partnerConfig.colors.primary }}>{formatPrice((supplier.cta_tax || 0) / 12)} €/Mois</div>
                  </td>
                ))}
              </tr>
              
              {/* TICGN */}
              <tr className="border-b">
                <td className="p-3 border border-gray-300 font-semibold bg-gray-50">
                  <span className="flex items-center">
                    TICGN
                    <InfoIcon text="Taxe Intérieure de Consommation sur le Gaz Naturel : Taxe perçue par l'État sur la consommation de gaz" />
                  </span>
                </td>
                {validSuppliers.slice(0, 3).map((supplier, index) => (
                  <td key={index} className="p-3 border border-gray-300 text-center">
                    <div className="font-semibold text-base">{formatPrice(supplier.ticgn_tax || 0)} €</div>
                    <div className="text-xs mt-1" style={{ color: partnerConfig.colors.primary }}>
                      {formatPrice((supplier.ticgn_tax || 0) / (offer.client_info.consumption / 1000))} €/MWh
                    </div>
                  </td>
                ))}
              </tr>
              
              {/* Total Taxes */}
              <tr className="border-b" style={{ backgroundColor: '#f0f0f0' }}>
                <td className="p-3 border border-gray-300 font-bold">TOTAL TAXES</td>
                {validSuppliers.slice(0, 3).map((supplier, index) => (
                  <td key={index} className="p-3 border border-gray-300 text-center font-bold text-base">
                    {formatPrice(supplier.total_taxes || 0)} €
                  </td>
                ))}
              </tr>
              
              {/* Total HT */}
              <tr className="border-b" style={{ backgroundColor: '#E8F4F8' }}>
                <td className="p-3 border border-gray-300 font-bold">TOTAL HT</td>
                {validSuppliers.slice(0, 3).map((supplier, index) => (
                  <td key={index} className="p-3 border border-gray-300 text-center font-bold text-base" style={{ color: partnerConfig.colors.primary }}>
                    {formatPrice(supplier.total_ht || 0)} €
                  </td>
                ))}
              </tr>
              
              {/* TVA 20% */}
              <tr className="border-b">
                <td className="p-3 border border-gray-300 font-semibold bg-gray-50">TVA 20%</td>
                {validSuppliers.slice(0, 3).map((supplier, index) => (
                  <td key={index} className="p-3 border border-gray-300 text-center font-semibold text-base">
                    {formatPrice(supplier.tva || 0)} €
                  </td>
                ))}
              </tr>
              
              {/* Total TTC */}
              <tr className="border-b" style={{ backgroundColor: partnerConfig.colors.primary, color: 'white' }}>
                <td className="p-3 border border-gray-300 font-bold">TOTAL TTC</td>
                {validSuppliers.slice(0, 3).map((supplier, index) => (
                  <td key={index} className="p-3 border border-gray-300 text-center font-bold text-base">
                    {formatPrice(supplier.total_ttc || 0)} €
                  </td>
                ))}
              </tr>
              
              {/* Evolution en % */}
              <tr className="border-b">
                <td className="p-3 border border-gray-300 font-semibold bg-gray-50">Evolution en %</td>
                {validSuppliers.slice(0, 3).map((supplier, index) => (
                  <td key={index} className="p-3 border border-gray-300 text-center font-semibold text-base" 
                      style={{ color: supplier.evolution_percentage < 0 ? '#22c55e' : '#ef4444' }}>
                    {supplier.evolution_percentage ? supplier.evolution_percentage.toFixed(2) : '0,00'}
                  </td>
                ))}
              </tr>
              
              {/* Soit en € */}
              <tr className="border-b">
                <td className="p-3 border border-gray-300 font-semibold bg-gray-50">Soit en €</td>
                {validSuppliers.slice(0, 3).map((supplier, index) => (
                  <td key={index} className="p-3 border border-gray-300 text-center font-semibold text-base">
                    {formatPrice(supplier.evolution_amount || 0)} €
                  </td>
                ))}
              </tr>
              
              {/* Garantie financière */}
              <tr className="border-b">
                <td className="p-3 border border-gray-300 font-semibold bg-gray-50">Garantie financière</td>
                {validSuppliers.slice(0, 3).map((supplier, index) => (
                  <td key={index} className="p-3 border border-gray-300 text-center font-semibold text-base">
                    {formatPrice(supplier.financial_guarantee || 0)} €
                  </td>
                ))}
              </tr>
              
              {/* Option tarifaire */}
              <tr className="border-b">
                <td className="p-3 border border-gray-300 font-semibold bg-gray-50">Option tarifaire</td>
                {validSuppliers.slice(0, 3).map((supplier, index) => (
                  <td key={index} className="p-3 border border-gray-300 text-center font-semibold text-base">
                    {supplier.tariff_option || '-'}
                  </td>
                ))}
              </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
        </div>

        {/* Nouvelles échéances et tarification proposées */}
        <div className="mt-12 mb-8 page-break-avoid">
          <div className="text-white font-bold text-xl py-3 px-6 mb-6" style={{ backgroundColor: partnerConfig.colors.primary }}>
            NOUVELLES ÉCHÉANCES ET TARIFICATION PROPOSÉES
          </div>
          
          <div className={`border-2 rounded-lg p-6 ${validSuppliers.slice(0, 3).length === 1 ? 'max-w-sm mx-auto' : ''}`} style={{ borderColor: partnerConfig.colors.primary }}>
            <div className={`grid gap-0 divide-x ${validSuppliers.slice(0, 3).length === 1 ? 'grid-cols-1' : validSuppliers.slice(0, 3).length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`} style={{ divideColor: '#E5E5E5' }}>
              {validSuppliers.slice(0, 3).map((supplier, index) => (
                <div key={index} className="text-center px-4">
                  {/* Nom du fournisseur */}
                  <div className="font-bold text-lg mb-4" style={{ color: partnerConfig.colors.primary }}>
                    {supplier.name}
                  </div>
                  
                  {/* Date de fin de contrat */}
                  <div className="py-3 border-b border-gray-200">
                    <div className="text-gray-800 font-medium text-lg">
                      {supplier.contract_end_date || '-'}
                    </div>
                  </div>
                  
                  {/* Durée du contrat */}
                  <div className="py-3 border-b border-gray-200 bg-gray-50">
                    <div className="text-gray-800 font-medium text-lg">
                      {supplier.contract_duration ? `${supplier.contract_duration} mois` : '-'}
                    </div>
                  </div>
                  
                  {/* Stratégie de prix */}
                  <div className="py-3">
                    <div className="text-gray-800 font-medium text-lg uppercase">
                      {supplier.pricing_strategy || 'PRIX MARCHÉ FIXE'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="max-w-[1000px] mx-auto px-4 py-4 sm:px-6 sm:py-6 md:px-8 md:py-8 border-t text-center text-xs text-gray-500">
        <p>Cette offre est réalisée sur la base des informations transmises par le client.</p>
        <p className="mt-2">{offer.agency_name || partnerConfig.name} - {new Date(offer.created_at).toLocaleDateString('fr-FR')}</p>
        <p className="mt-2">
          <span style={{ color: partnerConfig.colors.primary }}>{offer.advisor_info?.phone || partnerConfig.contact.phone}</span> | {offer.advisor_info?.email || partnerConfig.contact.email} | {partnerConfig.contact.website}
        </p>
      </div>

      {/* Modal descriptif fournisseur - masqué à l'impression */}
      {showSupplierModal && selectedSupplier && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 no-print"
          onClick={() => setShowSupplierModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Bouton fermeture */}
            <button
              onClick={() => setShowSupplierModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-light leading-none"
              style={{ width: '30px', height: '30px' }}
            >
              ×
            </button>

            {/* Logo du fournisseur */}
            <div className="flex justify-center mb-6">
              {getSupplierLogo(selectedSupplier) && (
                <img 
                  src={getSupplierLogo(selectedSupplier)} 
                  alt={selectedSupplier}
                  className="h-20 w-auto object-contain"
                />
              )}
            </div>

            {/* Description */}
            <div className="text-gray-700 leading-relaxed">
              <p>{getSupplierDescription(selectedSupplier)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicOfferView;
